import { useEffect, useMemo, useState } from "react";
import { advisoryApi, isUnavailable, type ResultsBundle, type Snapshot, type StatusStep } from "../advisoryApi";
import { stepLabel } from "../format";
import { SnapshotView, XRAY_PARTS, assetClassesOf } from "./SnapshotViews";
import type { StepProps } from "./common";

/**
 * Analytics / X-ray pages — QP's display snapshots (portfolio_display,
 * portfolio_forecast, expected_value, historical_return, cost_chart,
 * asset_allocation, portfolio_xray). QP lays them out as a tabs block keyed by
 * `tabId`; the same tabs are kept here. Snapshots are computed by QP whenever
 * the goal, risk or portfolio changes — nothing to save on this page.
 */
export function ResultsStep({ sessionId, steps, addr }: StepProps) {
  const [bundle, setBundle] = useState<ResultsBundle | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<string | null>(null);
  const statusKey = steps.map((s) => s.status).join(",");

  useEffect(() => {
    let stale = false;
    advisoryApi
      .results(sessionId)
      .then((b) => !stale && (setBundle(b), setErr(null)))
      .catch((e) =>
        !stale &&
        setErr(isUnavailable(e) ? "The results can't be loaded right now — the advice engine is not reachable." : (e as Error).message),
      );
    return () => {
      stale = true;
    };
  }, [sessionId, statusKey]);

  const isXray = steps.every((s) => s.componentType === "portfolio_xray");

  // Tabs: QP's tabId groups (Analytics), or the X-ray's own sections.
  const tabs = useMemo(() => {
    if (isXray) return XRAY_PARTS.map((p) => ({ key: p.key, label: p.label, steps: steps }));
    const groups = new Map<string, StatusStep[]>();
    for (const s of steps) {
      const k = s.tabId ?? s.componentId ?? s.componentType ?? "tab";
      groups.set(k, [...(groups.get(k) ?? []), s]);
    }
    return [...groups.entries()].map(([key, list]) => ({ key, label: list.map(stepLabel).join(" & "), steps: list }));
  }, [steps, isXray]);

  const active = tabs.find((t) => t.key === tab) ?? tabs[0];
  const allNa = steps.every((s) => s.status === "not_applicable");

  const portfolioFor = (s: StatusStep) => {
    const gr = bundle?.goals.find((g) => g.goal.id === (s.goalId ?? addr.goalId));
    return gr?.portfolios.find((p) => p.portfolio.id === (s.portfolioId ?? addr.portfolioId));
  };
  const snapshotFor = (s: StatusStep): Snapshot | undefined =>
    portfolioFor(s)?.snapshots.find((x) => x.snapshotType === s.snapshotType);

  if (allNa) {
    return (
      <section className="adv-step-card">
        <p className="adv-calm-note">
          The results appear once the portfolio is in place — the goal's risk assessment and the amount to invest are what
          they're calculated from.
        </p>
      </section>
    );
  }

  return (
    <section className="adv-step-card">
      {tabs.length > 1 && (
        <div className="tabs adv-tabs-tight" role="tablist">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active?.key === t.key}
              className={active?.key === t.key ? "tab-link active" : "tab-link"}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
      {err && <div className="banner-error">{err}</div>}
      {!bundle && !err && <p className="muted">Loading results…</p>}
      {bundle &&
        active &&
        (isXray ? (
          (() => {
            const snap = snapshotFor(steps[0]!);
            return snap ? <SnapshotView snapshot={snap} part={active.key} /> : <p className="muted">The X-ray is not ready yet.</p>;
          })()
        ) : (
          active.steps.map((s) => {
            const snap = snapshotFor(s);
            return (
              <div key={s.componentId ?? s.snapshotType} className="adv-subsection">
                {active.steps.length > 1 && <span className="adv-label">{stepLabel(s)}</span>}
                {snap ? (
                  <SnapshotView snapshot={snap} assetClassByIsin={assetClassesOf(portfolioFor(s)?.composition?.positions)} />
                ) : (
                  <p className="muted">{s.status === "not_applicable" ? "Not applicable to this portfolio." : "Being calculated…"}</p>
                )}
              </div>
            );
          })
        ))}
    </section>
  );
}
